import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Package, Trash2, AlertTriangle, Plus, Warehouse, Lock } from "lucide-react";
import { SalesSparkline } from "./SalesSparkline";
import { OrderImportanceBadge } from "./OrderImportanceBadge";

export const ProductList = ({
  products,
  loading,
  hasMore,
  totalProducts,
  searchQuery,
  onProductClick,
  onDeleteClick,
  onLoadMore,
  onAddProduct,
  selectedProducts = new Set(),
  onToggleSelection,
  onToggleYardStock,
}) => {
  if (products.length === 0 && !loading) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <Package className="h-16 w-16 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">No products found</p>
          <Button onClick={onAddProduct} className="mt-4 bg-green-600 hover:bg-green-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Product
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid gap-3">
        {products.map((product) => (
          <Card
            key={product.id}
            className={`cursor-pointer hover:shadow-md transition-shadow ${
              product.auto_calculate_stock_levels && !product.has_sales_data ? 'border-orange-300 bg-orange-50' : ''
            } ${selectedProducts.has(product.id) ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
            onClick={() => onProductClick(product)}
            data-testid={`product-${product.id}`}
          >
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1 flex gap-3">
                  {/* Selection Checkbox */}
                  {onToggleSelection && (
                    <div 
                      className="flex items-center pr-2" 
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      <Checkbox
                        checked={selectedProducts.has(product.id)}
                        onCheckedChange={() => {
                          onToggleSelection(product.id);
                        }}
                        className="h-5 w-5 border-2"
                        data-testid={`select-product-${product.id}`}
                      />
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-semibold text-lg">{product.name}</h3>
                      <OrderImportanceBadge level={product.order_importance} testId={`product-importance-${product.id}`} />
                      {product.plu_code && (
                        <span className="text-sm text-gray-600">PLU: {product.plu_code}</span>
                      )}
                      {product.vat_category === "Zero-rated 0%" && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded font-medium">
                          0% VAT
                        </span>
                      )}
                      {product.is_consignment && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-medium">
                          CONSIGNMENT
                        </span>
                      )}
                      {product.direct_order && (
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                          DIRECT ORDER
                        </span>
                      )}
                      {/* Yard-stock toggle: cashier ticks this on the Stock
                          Master list (no need to open the edit modal) to
                          flag products that live outside the shop. Any cart
                          containing a yard-stock item auto-prints 2 receipt
                          copies so yard staff can confirm payment. */}
                      {onToggleYardStock && (
                        <label
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded cursor-pointer select-none border ${
                            product.is_yard_stock
                              ? "bg-amber-100 text-amber-800 border-amber-300"
                              : "bg-white text-gray-500 border-gray-200 hover:border-amber-300 hover:text-amber-700"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          title="Yard / outside-shop stock — prints 2 receipt copies at the till"
                        >
                          <Checkbox
                            checked={!!product.is_yard_stock}
                            onCheckedChange={() => onToggleYardStock(product)}
                            className="h-3.5 w-3.5"
                            data-testid={`toggle-yard-stock-${product.id}`}
                          />
                          <Warehouse className="h-3 w-3" />
                          YARD
                        </label>
                      )}
                      {product.track_expiry && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                          EXPIRY TRACKED
                        </span>
                      )}
                      {product.sold_per_kg && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                          PER KG
                        </span>
                      )}
                      {product.no_discount && (
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">
                          NO DISCOUNT
                        </span>
                      )}
                      {!product.no_discount && product.discount_percent_allowed > 0 && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                          MAX {product.discount_percent_allowed}% OFF
                        </span>
                      )}
                      {product.auto_calculate_stock_levels && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                          AUTO
                        </span>
                      )}
                      {product.auto_calculate_stock_levels && !product.has_sales_data && (
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          NEEDS DATA
                        </span>
                      )}
                      {(product.current_stock || 0) === 0 && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded font-medium">
                          NO STOCK
                        </span>
                      )}
                      {/* Product Status Badges */}
                      {product.is_blocked && (
                        <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded font-bold">
                          BLOCKED
                        </span>
                      )}
                      {product.is_discontinued && (
                        <span className="px-2 py-0.5 bg-orange-500 text-white text-xs rounded font-bold">
                          DISCONTINUED
                        </span>
                      )}
                      {product.is_temp_blocked && (
                        <span className="px-2 py-0.5 bg-yellow-500 text-white text-xs rounded font-bold">
                          TEMP BLOCKED
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-6 gap-4 mt-2 text-sm items-center">
                      <div>
                        <span className="text-gray-600">Stock:</span> {product.sold_per_kg ? product.current_stock.toFixed(3) : product.current_stock} {product.sold_per_kg ? 'kg' : product.unit}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-600">Price:</span> R {(product.selling_price_incl_vat || product.retail_price_level1 || 0).toFixed(2)}
                        {product.price_locked && (
                          <Lock className="h-3.5 w-3.5 text-sky-600" title="Frozen price" data-testid={`product-price-locked-${product.plu_code}`} />
                        )}
                      </div>
                      <div>
                        <span className="text-gray-600">Cost:</span> R {(product.last_cost_price_excl_vat || product.cost_price || 0).toFixed(2)}
                      </div>
                      <div>
                        <span className="text-gray-600">Markup:</span> {(product.markup_percent || 0).toFixed(1)}%
                      </div>
                      <div className="flex items-center gap-2" title="Units sold YTD (live sales, excl. voids/refunds) — all-time total in brackets includes legacy imported sales">
                        <span className="text-blue-600 font-medium">Sold:</span>{" "}
                        <span className="text-blue-700 font-semibold">{Number(product.qty_sold_ytd || 0).toFixed(0)}</span>
                        <span className="text-blue-500 text-[11px]">YTD</span>
                        {Number(product.total_qty_sold || 0) !== Number(product.qty_sold_ytd || 0) && (
                          <span className="text-gray-500 text-[11px]">
                            ({Number(product.total_qty_sold || 0).toFixed(0)} all-time)
                          </span>
                        )}
                        {product.sales_sparkline && (
                          <span className="ml-1" title="6-month sales velocity (oldest → newest)">
                            <SalesSparkline values={product.sales_sparkline} />
                          </span>
                        )}
                      </div>
                      {(() => {
                        // Stock levels row — only render when at least one of the
                        // three levels is configured. Colour-code by health so the
                        // till user sees at a glance whether to reorder.
                        const minL = Number(product.minimum_level || 0);
                        const reL = Number(product.reorder_level || 0);
                        const maxL = Number(product.maximum_level || 0);
                        if (minL <= 0 && reL <= 0 && maxL <= 0) return null;
                        const stock = Number(product.current_stock || 0);
                        const tone =
                          stock <= minL ? "text-red-700 bg-red-50" :
                          stock <= reL ? "text-amber-700 bg-amber-50" :
                          "text-emerald-700 bg-emerald-50";
                        return (
                          <div className={`col-span-2 text-xs rounded px-1.5 py-0.5 inline-flex items-center gap-2 w-fit ${tone}`}>
                            <span className="font-medium">Stock levels:</span>
                            <span title="Minimum level">Min {minL.toFixed(0)}</span>
                            <span className="opacity-50">·</span>
                            <span title="Reorder level">Reorder {reL.toFixed(0)}</span>
                            <span className="opacity-50">·</span>
                            <span title="Maximum level">Max {maxL.toFixed(0)}</span>
                          </div>
                        );
                      })()}
                      <div className="text-right text-xs">
                        {(product.shelf_number || product.bin_number) ? (
                          <span className="text-purple-600 font-medium">
                            {product.shelf_number && `Shelf: ${product.shelf_number}`}
                            {product.shelf_number && product.bin_number && ' / '}
                            {product.bin_number && `Bin: ${product.bin_number}`}
                          </span>
                        ) : (
                          <span className="text-gray-400">No location</span>
                        )}
                      </div>
                      <div className="flex justify-end col-span-6 -mt-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => onDeleteClick(e, product)}
                          className={`h-8 w-8 p-0 ${
                            (product.current_stock || 0) === 0 
                              ? 'text-red-500 hover:text-red-700 hover:bg-red-50' 
                              : 'text-gray-300 cursor-not-allowed'
                          }`}
                          title={(product.current_stock || 0) === 0 ? 'Delete product' : 'Cannot delete - Stock must be 0'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">{product.category}</div>
                  <div className={`text-xs mt-1 ${product.vat_category === "Zero-rated 0%" ? "text-green-600 font-medium" : "text-gray-500"}`}>
                    {product.vat_category || "Standard 15%"}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Load More Button */}
      {hasMore && products.length > 0 && (
        <div className="flex justify-center mt-6">
          <Button
            onClick={onLoadMore}
            disabled={loading}
            variant="outline"
            className="px-8"
            data-testid="load-more-button"
          >
            {loading ? "Loading..." : `Load More (${products.length} of ${totalProducts})`}
          </Button>
        </div>
      )}
    </>
  );
};
